function parseDDMMYYYY(dateStr) {
  if (typeof dateStr === 'string' && dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
}

function daysBetween(date1, date2) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0,0,0,0);
  d2.setHours(0,0,0,0);
  return Math.floor((d2 - d1) / (1000 * 60 * 60 * 24));
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

export function calculateLoanEnd(principal, rate, startDate, endDate, emi) {
  const monthlyRate = (rate / 100) / 12; // Convert annual rate to monthly decimal rate
  const totalMonths = Math.ceil(daysBetween(startDate, endDate) / 30.44); // Approximate months

  // If EMI is not provided, calculate it using the loan period
  if (!emi && totalMonths > 0) {
    emi = monthlyRate === 0
      ? principal / totalMonths
      : (principal * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);
  }

  let remainingPrincipal = principal;
  let totalInterest = 0;

  // Calculate month by month to account for reducing balance
  for (let i = 0; i < totalMonths; i++) {
    const monthlyInterest = remainingPrincipal * monthlyRate;
    totalInterest += monthlyInterest;
    remainingPrincipal -= (emi - monthlyInterest);
  }

  const totalAmount = principal + totalInterest;

  return {
    totalInterest: Math.max(0, totalInterest),
    finalAmount: Math.max(principal, totalAmount)
  };
}

export const calculateLoanDetails = (loan, payments = []) => {
  if (!loan || !loan.principal_amount) {
    return {
      principalPaid: 0,
      interestPaid: 0,
      totalInterestAccrued: 0,
      remainingPrincipal: 0,
      totalPaid: 0,
      totalInterestAtEnd: 0,
      finalAmountAtEnd: 0,
    };
  }

  const principal = loan.principal_amount;
  const rate = loan.interest_rate / 100;
  const startDate = parseDDMMYYYY(loan.start_date);
  const today = new Date();
  const tenureMonths = loan.tenure_months || 0;
  const endDate = loan.end_date ? parseDDMMYYYY(loan.end_date) : addMonths(startDate, tenureMonths);
  const emi = loan.emi_amount ? parseFloat(loan.emi_amount) : 0;

  let accruedInterest = 0;
  let outstandingPrincipal = principal;
  let interestPaid = 0;
  let principalPaid = 0;
  let totalPaid = 0;

  // Sort payments by date
  const sortedPayments = [...payments].sort((a, b) => parseDDMMYYYY(a.payment_date) - parseDDMMYYYY(b.payment_date));

  let totalInterestAtEnd = 0;
  let finalAmountAtEnd = 0;

  // Diminishing balance method
    // Diminishing: compound interest at interval (quarterly default)
    const compoundingMonths = loan.compounding === 'monthly' ? 1 : 3;
    let lastDate = new Date(startDate);
    let nextCompoundDate = addMonths(lastDate, compoundingMonths);
    let paymentIdx = 0;
    let payment = sortedPayments[paymentIdx];
    let totalAccruedInterest = 0;
    while (lastDate < today) {
      let nextEventDate = today;
      let isPaymentEvent = false;
      if (payment && parseDDMMYYYY(payment.payment_date) <= today) {
        // Payments dated on/before the last processed date (same-day payments, or
        // payments on/before the start date) are applied immediately.
        const paymentDate = parseDDMMYYYY(payment.payment_date);
        nextEventDate = paymentDate > lastDate ? paymentDate : new Date(lastDate);
        isPaymentEvent = true;
      }
      if (nextCompoundDate > lastDate && nextCompoundDate <= today && nextCompoundDate < nextEventDate) {
        nextEventDate = nextCompoundDate;
        isPaymentEvent = false;
      }
      const days = daysBetween(lastDate, nextEventDate);
      let periodInterest = 0;
      if (days > 0) {
        periodInterest = outstandingPrincipal * rate * (days / 365);
        accruedInterest += periodInterest;
      }
      if (isPaymentEvent) {
        let amt = parseFloat(payment.amount) || 0;
        totalPaid += amt;
        const interestComponent = Math.min(amt, accruedInterest);
        interestPaid += interestComponent;
        accruedInterest -= interestComponent;
        amt -= interestComponent;
        const principalComponent = Math.min(amt, outstandingPrincipal);
        principalPaid += principalComponent;
        outstandingPrincipal -= principalComponent;
        lastDate = nextEventDate;
        paymentIdx++;
        payment = sortedPayments[paymentIdx];
      } else if (nextCompoundDate.getTime() === nextEventDate.getTime()) {
        outstandingPrincipal += accruedInterest;
        totalAccruedInterest += accruedInterest;
        accruedInterest = 0;
        lastDate = nextCompoundDate;
        nextCompoundDate = addMonths(nextCompoundDate, compoundingMonths);
      } else {
        lastDate = today;
        totalAccruedInterest += accruedInterest;
      }
    }
    accruedInterest = totalAccruedInterest;
    // Calculate total interest and final amount at end (full schedule, no prepayments)
    // calculateLoanEnd expects the annual rate in percent (it divides by 100 itself)
    const end = calculateLoanEnd(principal, loan.interest_rate, startDate, endDate, emi);
    totalInterestAtEnd = end.totalInterest;
    finalAmountAtEnd = end.finalAmount;

  return {
    principalPaid: Math.max(0, principalPaid),
    interestPaid: Math.max(0, interestPaid),
    totalInterestAccrued: Math.max(0, interestPaid + accruedInterest),
    remainingPrincipal: Math.max(0, outstandingPrincipal),
    totalPaid: totalPaid,
    totalInterestAtEnd: totalInterestAtEnd,
    finalAmountAtEnd: finalAmountAtEnd,
  };
};
